import React from "react";
import { isNativeShell } from "../config/appConfig";
import { applyThemeTokens, normalizeUiPreferences, resolveThemeMode } from "../lib/theme";

export function useAppPreferences(session) {
  const [prefersDark, setPrefersDark] = React.useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
      : true
  );
  const [themeRevision, setThemeRevision] = React.useState(0);

  const currentUiPreferences = React.useMemo(
    () => normalizeUiPreferences(session?.uiPreferences),
    [session?.uiPreferences]
  );

  const currentThemeMode = resolveThemeMode(currentUiPreferences, prefersDark);

  const effectiveFontScale = React.useMemo(() => {
    const baseScale = Number(currentUiPreferences.fontScale || 100) || 100;
    if (isNativeShell()) {
      const nativeBoost = 10;
      return Math.max(85, Math.min(125, baseScale + nativeBoost));
    }
    const webAdjustedScale = baseScale - 10;
    return Math.max(75, Math.min(115, webAdjustedScale));
  }, [currentUiPreferences.fontScale]);

  const currentZoom = Number(effectiveFontScale / 100) || 1;

  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (event) => setPrefersDark(!!event.matches);
    setPrefersDark(!!media.matches);
    if (typeof media.addEventListener === "function") media.addEventListener("change", handler);
    else if (typeof media.addListener === "function") media.addListener(handler);
    return () => {
      if (typeof media.removeEventListener === "function") media.removeEventListener("change", handler);
      else if (typeof media.removeListener === "function") media.removeListener(handler);
    };
  }, []);

  React.useEffect(() => {
    applyThemeTokens(currentThemeMode);
    setThemeRevision((v) => v + 1);
  }, [currentThemeMode]);

  React.useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.style.setProperty("--fz-app-zoom", String(currentZoom));
    document.documentElement.style.setProperty("--fz-text-size-adjust", `${effectiveFontScale}%`);
    document.documentElement.style.webkitTextSizeAdjust = `${effectiveFontScale}%`;
    document.documentElement.style.textSizeAdjust = `${effectiveFontScale}%`;
    document.body.style.webkitTextSizeAdjust = `${effectiveFontScale}%`;
    document.body.style.textSizeAdjust = `${effectiveFontScale}%`;
  }, [currentZoom, effectiveFontScale]);

  return {
    prefersDark,
    themeRevision,
    currentUiPreferences,
    currentThemeMode,
    effectiveFontScale,
    currentZoom,
  };
}
